import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { badgeSizes } from '../lib/badgeColors';
import { Tooltip } from './Tooltip';

type MetaBadgeSize = keyof typeof badgeSizes; // 'sm' | 'xs' | 'xxs'

// 편집 가능한 메타 배지/칩에 붙이는 작은 leading 점(클릭해 편집 가능하다는 affordance).
// 정적 MetaBadge는 `dot` prop으로, 편집형 Select 칩은 buttonClassName에 직접 이 클래스를 쓴다(점 모양 단일 출처).
export const metaBadgeEditableDotClass =
  "before:mr-1 before:inline-block before:size-1.5 before:shrink-0 before:rounded-full before:bg-current before:opacity-40 before:content-['']";

type MetaBadgeProps = {
  // 색 className. badgeColors의 엔티티 배지 맵(예: documentVisibilityBadge[v], planTypeBadge[t])이나
  // 범용 badgeTone.neutral 등을 그대로 넘긴다. 없으면 모양만 적용된다.
  tone?: string;
  size?: MetaBadgeSize;
  className?: string;
  // 배지가 나타내는 축 라벨(예: "공개 범위"). 주면 hover/포커스 시 공용 Tooltip으로 띄운다.
  tooltip?: string;
  // true면 편집 가능 affordance로 작은 점을 leading에 붙인다.
  dot?: boolean;
  // 주면 배지가 button으로 렌더된다. 배지 자체가 동작을 여는 경우(예: 레거시 키 폐기)에만 쓰고,
  // 클릭 가능하다는 신호가 필요하므로 `dot`·`tooltip`과 함께 쓰는 것을 기본으로 한다.
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
};

// 상태/메타용 공통 알약 배지. 모양(badgeSizes)을 한 곳에서 관리하고 색은 tone으로 받는다.
// 인라인 편집형 배지(Select)는 이 컴포넌트가 아니라 Select buttonClassName에 같은 tone을 쓴다.
export const MetaBadge = ({
  tone,
  size = 'sm',
  className,
  tooltip,
  dot,
  onClick,
  disabled,
  children,
}: MetaBadgeProps) => {
  const shapeClass = cn(badgeSizes[size], tone, dot && metaBadgeEditableDotClass, className);
  const badge = onClick ? (
    <button
      type="button"
      className={cn(shapeClass, 'transition-opacity hover:opacity-80 disabled:opacity-60')}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  ) : (
    <span className={shapeClass}>{children}</span>
  );
  if (!tooltip) return badge;
  return (
    <Tooltip content={tooltip} mode="overflowSafe" placement="top" className="inline-flex">
      {badge}
    </Tooltip>
  );
};
